import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'
import type {
  newWords,
  WordProgress,
  WordsShouldBeReviewed
} from '@/views/vocab/types'
import type { reviewCollection } from '@/views/vocab/collection-review/types'

type ExtraResumeContext = { collectionId: string; userId: string; sessionGeneration: number }

export const useVocabStore = defineStore(
  'vocabulary',
  () => {
    const vocabCollection = ref<WordsShouldBeReviewed>({
      collection: 'daily',
      words: [],
      progress: {
        reviewed: 0,
        total: 0
      }
    })
    const targetWord = ref<WordProgress | newWords>({} as WordProgress)
    const dailyVocabsView = ref<'stacked' | 'grid'>('stacked')
    const extraVocabsReviewed = ref(0)
    const extraResumeContext = ref<ExtraResumeContext | null>(null)
    const reviewCollection = reactive<reviewCollection>({
      mode: 'CATEGORY',
      category: ''
    })

    function storeVocabCollection(payload: WordsShouldBeReviewed) {
      vocabCollection.value = payload
      const selected = payload.words.find((item) => item.word.id === targetWord.value?.word?.id)
      targetWord.value = selected ?? payload.words[0] ?? ({} as WordProgress)
    }

    function storeVocabCollectionProgress(wordId: string) {
      const index = vocabCollection.value.words.findIndex((row) => row.word.id === wordId)
      if (index < 0) return
      vocabCollection.value.words.splice(index, 1)
      vocabCollection.value.progress.reviewed++
      targetWord.value = vocabCollection.value.words[index] ?? vocabCollection.value.words[0] ?? ({} as WordProgress)
    }

    function setTargetWord(payload: WordProgress | newWords) {
      targetWord.value = { ...payload }
    }
    function setDailyVocabsView(payload: 'stacked' | 'grid') {
      dailyVocabsView.value = payload === 'grid' ? 'grid' : 'stacked'
    }
    function setExtraVocabsReviewed() {
      extraVocabsReviewed.value++
    }
    function resetExtraVocabsReviewed(payload: number) {
      extraVocabsReviewed.value = payload
    }
    function setReviewCollection(payload: reviewCollection) {
      reviewCollection.mode = payload.mode
      reviewCollection.category = payload.category
    }

    function preserveExtraForWordDetail(context: ExtraResumeContext) {
      extraResumeContext.value = context
    }

    function clearExtraResume() {
      extraResumeContext.value = null
    }

    function consumeExtraResume(context: ExtraResumeContext): boolean {
      const saved = extraResumeContext.value
      extraResumeContext.value = null
      if (!saved) return false
      const matches = Boolean(context.userId) && saved.collectionId === context.collectionId &&
        saved.userId === context.userId && saved.sessionGeneration === context.sessionGeneration &&
        vocabCollection.value.collection === 'extra'
      if (!matches) clearAccountData()
      return matches
    }

    function clearAccountData() {
      extraResumeContext.value = null
      vocabCollection.value = { collection: 'daily', words: [], progress: { reviewed: 0, total: 0 } }
      targetWord.value = {} as WordProgress
      extraVocabsReviewed.value = 0
      reviewCollection.mode = 'CATEGORY'
      reviewCollection.category = ''
    }

    return {
      clearAccountData,
      preserveExtraForWordDetail,
      clearExtraResume,
      consumeExtraResume,
      vocabCollection,
      storeVocabCollection,
      storeVocabCollectionProgress,
      targetWord,
      setTargetWord,
      dailyVocabsView,
      setDailyVocabsView,
      extraVocabsReviewed,
      setExtraVocabsReviewed,
      resetExtraVocabsReviewed,
      reviewCollection,
      setReviewCollection
    }
  },
  {
    persist: {
      paths: ['dailyVocabsView'],
      afterRestore: ({ store }) => {
        if (store.dailyVocabsView !== 'stacked' && store.dailyVocabsView !== 'grid') {
          store.setDailyVocabsView('stacked')
          store.$persist()
        }
      }
    }
  }
)
